import React, { useState } from "react";
import { Alert, Button, List, Modal, Tag, Typography } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

// The window comes from the company setting (Company Profile → HR), the same one the
// Apply Leave / Short Leave date pickers and the server use
// — this default only applies if the backend didn't send it. Regularizing
// attendance has no such limit — only future dates are blocked there.
const DEFAULT_LEAVE_BACKDATE_DAYS = 7;

const canStillApplyLeave = (date, windowDays) =>
  !dayjs(date).isBefore(dayjs().subtract(windowDays, "day").startOf("day"));

// Shared banner for the "unmarked attendance" warning shown in the Leave
// Summary drawer, the Employee page's Leaves Information panel and the Apply
// Leave / Short Leave modals. The dates are only for the current leave year —
// the backend (calculateLeaveSummary) scopes them to the same cycle as the count.
const UnmarkedAttendanceAlert = ({ unmarkedAttendance, style }) => {
  const [open, setOpen] = useState(false);

  if (!(unmarkedAttendance?.count > 0)) return null;

  const { count, dates = [], deductionEnabled, deductionLabel, projectedBalance } = unmarkedAttendance;
  const leaveWindowDays = unmarkedAttendance.leaveBackdateDays ?? DEFAULT_LEAVE_BACKDATE_DAYS;

  return (
    <>
      <Alert
        type="warning"
        showIcon
        style={style}
        message={`${count} unmarked attendance day${count !== 1 ? "s" : ""}`}
        description={
          <>
            <div>
              {deductionEnabled
                ? `Please regularize these days or apply leave — otherwise they will be automatically deducted as ${deductionLabel}.`
                : "Please regularize these days."}
            </div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
              Regularizing marks your attendance for the day. Leave can only be applied for the
              last {leaveWindowDays} day{leaveWindowDays !== 1 ? "s" : ""}; for older days, HR can apply it on your behalf.
            </div>
            {projectedBalance !== null && projectedBalance !== undefined && (
              <div style={{ marginTop: 6 }}>
                <Text strong>
                  Projected {deductionLabel} balance if not regularised: {projectedBalance} day
                  {projectedBalance !== 1 ? "s" : ""}
                </Text>
              </div>
            )}
            {dates.length > 0 && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginTop: 4 }}
                onClick={() => setOpen(true)}
              >
                View dates
              </Button>
            )}
          </>
        }
      />
      <Modal
        title="Unmarked Attendance Dates"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={400}
      >
        <List
          size="small"
          dataSource={dates}
          renderItem={(d) => (
            <List.Item
              extra={
                canStillApplyLeave(d, leaveWindowDays) ? (
                  <Tag color="blue">Regularize or apply leave</Tag>
                ) : (
                  <Tag>Regularize, or ask HR to apply leave</Tag>
                )
              }
            >
              {dayjs(d).format("ddd, DD MMM YYYY")}
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default UnmarkedAttendanceAlert;
